import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthValidation } from '@/hooks/useAuthValidation';
import { useLogging } from '@/shared/hooks/useLogging';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { WhatsAppConversationWindowStatus } from '../whatsapp/WhatsAppConversationWindowStatus';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { AIAgentModeSelector } from './AIAgentModeSelector';
import { WhatsAppErrorPanel } from './WhatsAppErrorPanel';
import { AIAnimationTest } from '@/components/debug/AIAnimationTest';
import { AISuggestionDebugger } from '@/components/debug/AISuggestionDebugger';
import { AISuggestionBar } from './AISuggestionBar';
import { AIModeFlowTest } from '@/components/debug/AIModeFlowTest';
import { AISuggestionTester } from '@/components/debug/AISuggestionTester';
import { supabase } from '@/integrations/supabase/client';

// Import our new hooks and services
import { useMessageData } from '@/hooks/useMessageData';
import { useMessageSending } from '@/hooks/useMessageSending';
import { useRealtimeMessages } from '@/hooks/useRealtimeMessages';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { useAIAgentMode } from '@/hooks/useAIAgentMode';

import { markTenantMessagesAsRead } from '@/lib/tenant-conversations-client';
import { RealtimeStatus } from './RealtimeStatus';
import { AIProcessingStatus } from './AIProcessingStatus';
import { useAIProcessingState } from '@/hooks/useAIProcessingState';
import { useAISuggestionsContext } from '@/contexts/AISuggestionsContext';
import { useEffectiveTenantId } from '@/hooks/useEffectiveTenantId';
import { useConversationResetMarkers } from '@/hooks/useConversationResetMarkers';
import { AIAutoModeStatus } from '@/components/chat/AIAutoModeStatus';
import type { MessageUpdate } from '@/types/message-types';
import type { MediaFile } from '@/services/mediaUploadService';
interface RealtimeChatProps {
  conversationId: string | null;
  customerId: string;
  customerName?: string;
  customerPhone?: string;
  overrideTenantId?: string;
  onMessageSent?: (message?: unknown) => void;
  onMessageUpdate?: (update: MessageUpdate) => void;
  onAIModeChange?: (conversationId: string, newMode: 'auto' | 'suggestions' | 'manual') => void;
  testMode?: boolean;
  onTestMessageSend?: (messageContent: string) => void;
  // V1411: Draft persistence
  initialDraft?: string;
  onDraftChange?: (draft: string) => void;
  onDraftClear?: () => void;
  // Phase 1A: Mark as Resolved
  inboxCategory?: string;
  onInboxCategoryChange?: (conversationId: string, newCategory: string) => void;
  // Customer-centric: subscribe to ALL conversations for this customer
  allConversationIds?: string[];
  // N-14: the caller already renders its own compact header with the customer's
  // name and phone (e.g. Conversations.tsx on phone widths, which also needs the
  // room for a back button). Passing true skips that same name/phone row here so
  // the conversation header does not render twice, stacked one bar on the other.
  hideCustomerIdentity?: boolean;
}
export function RealtimeChat({
  conversationId,
  customerId,
  customerName = 'Customer',
  customerPhone,
  overrideTenantId,
  onMessageSent,
  onMessageUpdate,
  onAIModeChange,
  testMode = false,
  onTestMessageSend,
  // V1411: Draft persistence
  initialDraft,
  onDraftChange,
  onDraftClear,
  // Phase 1A: Mark as Resolved
  inboxCategory,
  onInboxCategoryChange,
  // Customer-centric conversations
  allConversationIds,
  hideCustomerIdentity = false
}: RealtimeChatProps) {
  
  // Debug log for customer phone
  console.log('🔍 RealtimeChat customerPhone debug:', {
    customerPhone,
    conversationId,
    customerId
  });
  const {
    user,
    userProfile
  } = useAuth();
  const {
    toast
  } = useToast();
  const { t } = useTranslation('conversations');
  const {
    error,
    debug
  } = useLogging('whatsapp');

  // UI State
  // V1411: Initialize from draft if provided
  const [newMessage, setNewMessage] = useState(initialDraft || '');
  const [showDebug, setShowDebug] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [hasMarkedAsRead, setHasMarkedAsRead] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [aiSuggestionTesterEnabled, setAiSuggestionTesterEnabled] = useState(false);
  const [isWindowOpen, setIsWindowOpen] = useState(true);

  // 🎯 [AI-ASSIST-FIX-V4] Track last processed message to prevent duplicate AI calls
  const lastProcessedMessageIdRef = useRef<string | null>(null);

  // Use effective tenant ID for impersonation support
  // Priority: overrideTenantId (test mode) > useEffectiveTenantId (impersonation/auth)
  const authTenantId = useEffectiveTenantId();
  const effectiveTenantId = overrideTenantId || authTenantId;

  // Load AI Suggestion Tester setting
  useEffect(() => {
    const loadAiTesterSetting = async () => {
      try {
        const {
          data: userData
        } = await supabase.auth.getUser();
        if (!userData.user) return;
        const {
          data: userProfile
        } = await supabase.from('users').select('tenant_id').eq('auth_user_id', userData.user.id).single();
        if (!userProfile?.tenant_id) return;
        const {
          data: setting
        } = await supabase.from('tenant_settings').select('setting_value').eq('tenant_id', userProfile.tenant_id).eq('setting_key', 'ai_suggestion_tester_enabled').maybeSingle();
        setAiSuggestionTesterEnabled(Boolean((setting?.setting_value as Record<string, unknown>)?.enabled ?? false));
      } catch (error) {
        console.error('Error loading AI tester setting:', error);
      }
    };
    loadAiTesterSetting();
  }, []);

  // AI suggestions context for unified state management
  const {
    currentSuggestion,
    generateAISuggestion,
    clearSuggestion,
    loading,
    clearSuggestionOnCustomerMessage,
    setConversationId,
    pollForAISuggestion,
    setCurrentSuggestion
  } = useAISuggestionsContext();

  // Create a stable wrapper function for AI mode hook that matches the expected signature
  // 🎯 [AI-ASSIST-FIX-V2] Accept optional unansweredMessages for mode switch scenario
  const generateAISuggestionWrapper = useCallback(async (messageContent: string, unansweredMessages?: unknown[]): Promise<void> => {
    debug('generateAISuggestionWrapper called', {
      messageContent: messageContent?.substring(0, 50),
      conversationId,
      unansweredCount: unansweredMessages?.length
    });
    try {
      const result = await generateAISuggestion(messageContent, {
        conversationId,
        customerId,
        customerPhone,
        tenantId: effectiveTenantId || '',
        bufferedMessages: unansweredMessages // Pass buffered messages to bypass buffer check
      });
      debug('generateAISuggestion result', {
        result: result ? 'Generated suggestion' : 'No suggestion'
      });
    } catch (error) {
      debug('generateAISuggestion error', error);
      throw error;
    }
  }, [conversationId, customerId, customerPhone, effectiveTenantId, generateAISuggestion, debug]);

  // AI Agent Mode Hook - with AI suggestion generation capability + Debug
  const {
    aiMode,
    loading: modeLoading,
    isProcessing: aiModeProcessing,
    changeMode,
    processUnansweredMessages
  } = useAIAgentMode({
    conversationId,
    generateAISuggestion: generateAISuggestionWrapper,
    onModeChange: onAIModeChange
  });

  // 🎯 [AI-ASSIST-FIX-V4] Reset processed message ref when conversation or mode changes
  useEffect(() => {
    lastProcessedMessageIdRef.current = null;
  }, [conversationId, aiMode]);

  // 🔧 DEBUG: Log AI mode changes only when they actually change
  useEffect(() => {
    console.log('🔧 RealtimeChat: AI Mode changed:', { 
      aiMode, 
      modeLoading, 
      conversationId,
      timestamp: new Date().toISOString()
    });
  }, [aiMode, modeLoading, conversationId]);

  // Use our enhanced data hook with infinite scroll support
  const {
    messages,
    isLoading,
    error: dataError,
    loadMessages,
    silentRefresh,
    addMessage,
    updateMessage,
    updateMessageByWhatsAppId,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore
  } = useMessageData(conversationId, overrideTenantId, customerId, allConversationIds);


  // Enhanced message handler that auto-clears suggestions on customer messages and triggers auto responses
  const handleAddMessage = useCallback((message: Record<string, unknown>) => {
    addMessage(message);
    
    // Handle customer messages based on AI mode
    if (message.sender_type === 'customer') {
      if (aiMode === 'suggestions') {
        console.log('🗨️ Customer message detected in suggestions mode, clearing existing suggestions');
        clearSuggestionOnCustomerMessage(message.id);

        // 🎯 [AI-ASSIST-FIX] Poll for AI suggestion from platform_metadata
        // Memory-buffer will consolidate messages and call workflow-executor
        // Workflow-executor will save suggestion to platform_metadata
        // We poll to detect and display the suggestion
        console.log('🎯 Starting poll for AI suggestion for customer message:', message.content?.substring(0, 50));
        pollForAISuggestion(conversationId, effectiveTenantId || '').then((suggestion) => {
          if (suggestion) {
            console.log('✅ AI suggestion received from poll:', suggestion.content?.substring(0, 50));
            setCurrentSuggestion(suggestion);
          } else {
            console.warn('⏰ No AI suggestion received within timeout');
          }
        }).catch((error) => {
          console.error('❌ Error polling for AI suggestion:', error);
        });
      } else if (aiMode === 'auto') {
        console.log('🤖 Customer message detected in auto mode, processing via workflow-executor only');
        // Note: AI Auto processing will be handled by useAIAgentMode's processUnansweredMessages
        // Processing is handled by workflow-executor via useAIAgentMode
      }
    }
  }, [addMessage, aiMode, clearSuggestionOnCustomerMessage, pollForAISuggestion, conversationId, effectiveTenantId, setCurrentSuggestion]);

  // AI processing state management - consolidated single flow for both auto and suggestions modes
  const {
    isProcessing: isAIProcessing,
    startProcessing,
    stopProcessing
  } = useAIProcessingState({
    aiMode,
    messages,
    conversationId,
    onProcessingStart: messageContent => {
      console.log('🔍 onProcessingStart callback called with:', { aiMode, messageContent });
      console.log('🤖 AI Processing started, routing based on mode:', {
        aiMode,
        messageContent: messageContent?.substring(0, 50)
      });
      
      if (aiMode === 'suggestions' && messageContent) {
        // AI Suggestions mode - clear existing suggestions first, then generate new ones
        console.log('🎯 Clearing existing suggestions before generating new ones');
        clearSuggestion();
        console.log('🎯 Triggering AI suggestion generation for suggestions mode');
        generateAISuggestion(messageContent, {
          conversationId,
          customerId,
          customerPhone,
          tenantId: effectiveTenantId || ''
        });
      } else if (aiMode === 'auto') {
        // Full Auto mode - trigger actual AI response via useAIAgentMode
        console.log('🚀 Full Auto mode - calling processUnansweredMessages');
        processUnansweredMessages();
      }
    }
  });

  // Debug log AI mode changes on first load only
  useEffect(() => {
    if (conversationId) {
      console.log('🎯 RealtimeChat: AI mode initialized', {
        aiMode,
        conversationId
      });
    }
  }, [conversationId]); // Only log when conversation changes

  // Create ref to store the addRecentlySentMessage function to avoid circular dependency
  const addRecentlySentMessageRef = useRef<((messageId: string) => void) | null>(null);
  const {
    sending,
    whatsappError,
    requiresTemplate,
    sendMessage,
    clearError
  } = useMessageSending({
    conversationId,
    customerId,
    customerPhone,
    onMessageSent,
    onMessageAdded: addMessage,
    onMessageUpdated: updateMessage,
    addRecentlySentMessage: (messageId: string) => {
      addRecentlySentMessageRef.current?.(messageId);
    },
    onLoadMessages: loadMessages
  });

  // Stable callbacks using useRef to prevent unnecessary re-renders
  const stableCallbacksRef = useRef({
    silentRefresh
  });

  // Update refs when functions change
  useEffect(() => {
    stableCallbacksRef.current.silentRefresh = silentRefresh;
  }, [silentRefresh]);

  // Stable AI mode ref
  const aiModeRef = useRef(aiMode);
  useEffect(() => {
    aiModeRef.current = aiMode;
  }, [aiMode]);

  // Stable notification handlers - simplified to only handle refresh and status
  const handleNewMessage = useCallback(notification => {
    console.log('🔔 REALTIME NOTIFICATION RECEIVED:', {
      notification,
      // conversationId removed - not needed (filtering happens in useRealtimeNotifications)
      aiMode: aiModeRef.current,
      timestamp: new Date().toISOString()
    });

    // Always trigger immediate message refresh first
    console.log('🔄 TRIGGERING SILENT REFRESH from notification');
    stableCallbacksRef.current.silentRefresh();

    // Note: AI processing is now handled solely by useRealtimeMessages → useAIProcessingState
    // This eliminates duplicate AI processing paths and consolidates all AI logic
  }, []);  // Empty deps - stable callback prevents subscription cleanup cascade
  const handleStatusUpdate = useCallback(notification => {
    console.log('📊 Message status update received:', notification);
    // Trigger a silent refresh to update message statuses
    stableCallbacksRef.current.silentRefresh();
  }, []);

  // Real-time notifications for immediate message updates
  const {
    cleanup: cleanupNotifications
  } = useRealtimeNotifications({
    conversationId,
    onNewMessage: handleNewMessage,
    onStatusUpdate: handleStatusUpdate,
    enabled: true
  });
  console.log('🔍 DEBUG: About to call useRealtimeMessages with:', {
    conversationId,
    effectiveTenantId,
    hasEffectiveTenantId: !!effectiveTenantId
  });
  const realtimeHook = useRealtimeMessages({
    conversationId,
    enabled: true,
    onMessageReceived: handleAddMessage, // Use enhanced handler for auto-clearing
    onMessageUpdated: updateMessage,
    onLoadMessages: loadMessages,
    onSilentRefresh: silentRefresh,
    aiMode,
    onAIProcessingStart: startProcessing,
    tenantId: effectiveTenantId, // Pass effective tenant ID for impersonation support
    allConversationIds,
    customerId
  });

  // Destructure hook values
  const {
    cleanup,
    addRecentlySentMessage,
    subscriptionHealthy,
    retryCount,
    authError,
    tenantSchema,
    forceReconnect,
    isTransitioning,
    dataQualityScore,
    poorQualityCount,
    forceSubscriptionRestart,
    connectionErrors,
    lastSuccessfulConnection,
    fallbackPollingActive
  } = realtimeHook;

  // "Conversation reset" markers for the chat thread — sourced from conversation_events, never
  // from messages (see src/hooks/useConversationResetMarkers.ts). tenantSchema comes from
  // useRealtimeMessages above; this hook is a no-op query until both it and conversationId exist.
  const { data: resetMarkers = [] } = useConversationResetMarkers(tenantSchema, conversationId);

  // Removed broadcast-based AI processing coordination - now uses UI message detection only
  useEffect(() => {
    addRecentlySentMessageRef.current = addRecentlySentMessage;
  }, [addRecentlySentMessage]);

  // Sync conversation ID to AI suggestions context and reset read state
  useEffect(() => {
    if (conversationId) {
      setConversationId(conversationId);
      setHasMarkedAsRead(false);
    }
  }, [conversationId, setConversationId]);

  // Check for unanswered messages when messages change - handle both suggestions and auto modes
  useEffect(() => {
    if ((aiMode === 'suggestions' || aiMode === 'auto') && messages.length > 0 && conversationId && !loading) {
      // Sort messages by created_at to get chronological order
      const sortedMessages = [...messages].sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      // Get the last message overall
      const lastMessage = sortedMessages[sortedMessages.length - 1];

      // Only trigger AI actions if the last message is from a customer
      if (lastMessage && lastMessage.sender_type === 'customer' && lastMessage.content?.trim()) {
        if (aiMode === 'suggestions') {
          // 🎯 [AI-ASSIST-FIX-V4] Prevent duplicate calls - only process each message once
          if (lastProcessedMessageIdRef.current === lastMessage.id) {
            console.log('🔄 AI SUGGESTION FLOW: Already processed message, skipping', {
              messageId: lastMessage.id,
              content: lastMessage.content?.substring(0, 30)
            });
            return;
          }

          // 🎯 [AI-ASSIST-FIX-V2] Find ALL consecutive customer messages until last agent/AI response
          // Go backwards and collect customer messages until we hit an agent response
          const unansweredMessages = [];
          for (let i = sortedMessages.length - 1; i >= 0; i--) {
            const msg = sortedMessages[i];
            if (msg.sender_type === 'customer') {
              unansweredMessages.unshift(msg); // Add to beginning to maintain chronological order
            } else if (msg.sender_type === 'human_agent' ||
                       msg.sender_type === 'ai_agent_automatic' ||
                       msg.sender_type === 'ai_with_human_review_no_changes' ||
                       msg.sender_type === 'ai_with_human_review_with_changes') {
              // Stop at last agent/AI response
              break;
            }
          }

          console.log('🚀 AI SUGGESTION FLOW: Detected unanswered customer messages', {
            count: unansweredMessages.length,
            firstMessage: unansweredMessages[0]?.content?.substring(0, 50),
            lastMessage: unansweredMessages[unansweredMessages.length - 1]?.content?.substring(0, 50),
            messages: unansweredMessages.map(m => ({
              id: m.id,
              content: m.content?.substring(0, 30),
              timestamp: m.created_at
            }))
          });

          // Mark this message as processed
          lastProcessedMessageIdRef.current = lastMessage.id;

          // Trigger AI suggestion generation with all unanswered messages
          generateAISuggestionWrapper(lastMessage.content, unansweredMessages);
        } else if (aiMode === 'auto') {
          console.log('🤖 AUTO MODE FLOW: Detected unanswered customer message, useAIAgentMode will handle processing', {
            messageId: lastMessage.id,
            content: lastMessage.content.substring(0, 50),
            timestamp: lastMessage.created_at,
            senderType: lastMessage.sender_type
          });
        }
      } else if (aiMode === 'suggestions') {
        console.log('🚀 AI SUGGESTION FLOW: Last message is not from customer, clearing suggestions', {
          lastMessageSender: lastMessage?.sender_type,
          lastMessageContent: lastMessage?.content?.substring(0, 50)
        });
        
        // Clear suggestions if the last message is not from customer (only for suggestions mode)
        clearSuggestion();
      }
    }
  }, [aiMode, messages, conversationId, loading, generateAISuggestionWrapper, clearSuggestion, effectiveTenantId, customerPhone]);

  // Cleanup notifications on unmount ONLY (not on callback changes)
  // Phase 2.5 FIX: Empty deps [] prevents cleanup cascade when callbacks get new references
  // The cleanup functions are called via refs, so they don't need to be dependencies
  useEffect(() => {
    // Capture current cleanup functions in a ref to avoid stale closures
    const currentCleanupNotifications = cleanupNotifications;
    const currentCleanup = cleanup;

    return () => {
      currentCleanupNotifications();
      currentCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - cleanup only on unmount, not on callback changes

  // Mark messages as read
  useEffect(() => {
    if (!hasMarkedAsRead && user && messages.length > 0) {
      markMessagesAsRead();
    }
  }, [messages.length, hasMarkedAsRead, user]);

  // V1411: Load initial draft when conversation changes or draft becomes available
  useEffect(() => {
    if (initialDraft && newMessage !== initialDraft) {
      console.log('[V1411] 📥 Loading draft into message input:', initialDraft.substring(0, 50));
      setNewMessage(initialDraft);
    }
  }, [conversationId, initialDraft]);

  // V1411: Sync draft changes to parent (debounced)
  useEffect(() => {
    if (onDraftChange && newMessage !== initialDraft) {
      const timeoutId = setTimeout(() => {
        onDraftChange(newMessage);
      }, 500); // 500ms debounce to avoid excessive updates

      return () => clearTimeout(timeoutId);
    }
  }, [newMessage, onDraftChange, initialDraft]);

  // Remove complex height measurement - CSS Grid handles layout

  const markMessagesAsRead = useCallback(async () => {
    if (!user) return;
    try {
      const {
        error
      } = await markTenantMessagesAsRead(conversationId, user.id);
      if (!error) {
        setHasMarkedAsRead(true);
      }
    } catch (err) {
      console.error('Error marking messages as read:', err);
      error('Failed to mark messages as read', {
        conversationId,
        error: err
      });
    }
  }, [conversationId, user]);
  const handleSendMessage = useCallback(async (content: string, messageType: string, modelId?: string, mediaFiles?: MediaFile[], aiSuggestionData?: Record<string, unknown>) => {
    // Test mode: call test callback instead of sending to WhatsApp
    if (testMode && onTestMessageSend) {
      onTestMessageSend(content);
      return;
    }

    // If this has AI suggestion data, we need to use a special sending mode
    if (aiSuggestionData) {
      await sendMessage(content, messageType, modelId, aiSuggestionData, mediaFiles);
    } else {
      await sendMessage(content, messageType, modelId, undefined, mediaFiles);
    }

    // V1411: Clear draft from parent after successful send
    if (onDraftClear) {
      onDraftClear();
    }

    // Notify parent component
    onMessageUpdate?.({
      type: 'sent',
      conversationId: conversationId || '',
      customerId,
      customerPhone,
      customerName,
      lastMessageContent: content,
      lastMessageAt: new Date().toISOString()
    });
  }, [testMode, onTestMessageSend, sendMessage, onMessageUpdate, conversationId, customerId, customerPhone, customerName]);
  const handleRefreshMessages = useCallback(async () => {
    setIsManualRefreshing(true);
    console.log('🔄 Manual refresh triggered');
    await loadMessages(true); // Force reload for manual refresh only
    setIsManualRefreshing(false);
    toast({
      title: t('messages.refreshed'),
      description: t('messages.refreshedDesc', { count: messages.length }),
      duration: 2000
    });
  }, [loadMessages, toast, messages.length, t]);

  // Force restart real-time connection
  const handleForceRestart = useCallback(() => {
    console.log('🔄 Force restart triggered');
    forceSubscriptionRestart();
    toast({
      title: t('messages.connectionRestarted'),
      description: t('messages.connectionRestartedDesc'),
      duration: 3000
    });
  }, [forceSubscriptionRestart, toast, t]);
  const handleCopyMessage = useCallback((content: string) => {
    navigator.clipboard.writeText(content);
    toast({
      title: t('messages.copied'),
      description: t('messages.copiedDesc'),
      duration: 2000
    });
  }, [toast, t]);
  const handleTemplateSelect = useCallback(() => {
    clearError();
    // Don't reload messages - real-time should handle new template messages
    console.log('✅ Template selected - relying on real-time updates');
  }, [clearError]);
  const handleReconnect = useCallback(() => {
    console.log('🔄 Manual reconnect triggered from UI');
    if (forceReconnect) {
      forceReconnect();
    } else {
      // Fallback to full cleanup and reload
      cleanup();
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }, [forceReconnect, cleanup]);

  // Show loading state or conversation prompt
  // In test mode, allow chat interface even without conversationId
  if (!conversationId && !testMode) {
    return <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-foreground mb-2">{t('chatEmptyState.selectConversationTitle')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('chatEmptyState.selectConversationDescription')}
          </p>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-full">
      {/* Chat Header - Fixed at top */}
      <Card className="flex-shrink-0">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-2">
            {/* Top row: Customer info and status indicators */}
            <div className={`flex items-center ${hideCustomerIdentity ? 'justify-end' : 'justify-between'}`}>
              {!hideCustomerIdentity && (
                <div className="flex items-baseline gap-3">
                  <h3 className="text-lg font-semibold">{customerName}</h3>
                  {customerPhone && <span className="text-sm text-muted-foreground">{customerPhone}</span>}
                </div>
              )}

              {/* Status indicators - connection and window status */}
              <div className="flex items-center gap-2">
                <RealtimeStatus isConnected={subscriptionHealthy} authError={authError} onReconnect={handleReconnect} retryCount={retryCount} tenantSchema={tenantSchema} isTransitioning={isTransitioning} dataQualityScore={dataQualityScore} poorQualityCount={poorQualityCount} onForceRestart={handleForceRestart} connectionErrors={connectionErrors} lastSuccessfulConnection={lastSuccessfulConnection} fallbackPollingActive={fallbackPollingActive} />
                {customerPhone && <WhatsAppConversationWindowStatus customerPhone={customerPhone} onStatusChange={setIsWindowOpen} />}

                {/* Phase 1A: Mark Resolved Button - Render independently of component error state */}
                {onInboxCategoryChange && conversationId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Default to 'agent_inbox' if inboxCategory is undefined (component error state)
                          const currentCategory = inboxCategory || 'agent_inbox';
                          const newCategory = currentCategory === 'resolved' ? 'agent_inbox' : 'resolved';
                          onInboxCategoryChange(conversationId, newCategory);
                        }}
                      >
                        <CheckCircle className={`h-4 w-4 ${inboxCategory === 'resolved' ? 'fill-green-500 text-green-500' : ''}`} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {inboxCategory === 'resolved'
                        ? t('inbox.reopen', 'Reopen conversation')
                        : t('inbox.markResolved', 'Mark as resolved')}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
            
            {/* Second row: AI controls */}
            <div className="flex items-center gap-2">
              <AIProcessingStatus isProcessing={isAIProcessing} />
              <AIAgentModeSelector currentMode={aiMode} loading={modeLoading} onModeChange={changeMode} />
            </div>
            
            {/* Debug section */}
            {showDebug && (
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <AIAnimationTest conversationId={conversationId} tenantSchema={tenantSchema} />
                  <span className="text-xs text-muted-foreground">
                    Schema: {tenantSchema}
                  </span>
                </div>
                {aiMode === 'suggestions' && process.env.NODE_ENV === 'development' && (
                  <div className="space-y-2">
                    <AISuggestionDebugger conversationId={conversationId} customerId={customerId} customerPhone={customerPhone} />
                    <AIModeFlowTest conversationId={conversationId} customerId={customerId} customerPhone={customerPhone} />
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>

      </Card>

      {/* Messages List - Takes remaining space */}
      <div className="flex-1 min-h-0">
        <MessageList messages={messages} isLoading={isLoading} customerName={customerName} conversationId={conversationId} tenantId={effectiveTenantId || undefined} onCopyMessage={handleCopyMessage} isManualRefresh={isManualRefreshing} whatsappError={whatsappError} dataError={dataError} showTemplateSelector={showTemplateSelector} onShowTemplateSelector={setShowTemplateSelector} onClearError={clearError} onTemplateSelect={handleTemplateSelect} aiMode={aiMode} isAIProcessing={false} // Remove from MessageList, now handled in header
      loadMoreMessages={loadMoreMessages} hasMoreMessages={hasMoreMessages} isLoadingMore={isLoadingMore} resetMarkers={resetMarkers} />
      </div>

      {/* AI Suggestion Tester - Only show if enabled */}
      {aiSuggestionTesterEnabled && <div className="flex-shrink-0 p-3 border-t bg-background">
          <AISuggestionTester conversationId={conversationId} customerId={customerId} customerPhone={customerPhone} currentSuggestion={currentSuggestion} loading={loading} generateAISuggestion={(message) => generateAISuggestion(message, { conversationId, customerId, customerPhone, tenantId: effectiveTenantId || '' })} clearSuggestion={clearSuggestion} />
        </div>}

      {/* Message Input or AI Auto Status - Sticky at bottom */}
      <div className="flex-shrink-0 sticky bottom-0 border-t bg-background">
        {aiMode === 'auto' && !testMode ? (
          <AIAutoModeStatus
            messages={messages}
            conversationId={conversationId}
          />
        ) : (
          <MessageInput conversationId={conversationId} messages={messages} newMessage={newMessage} onNewMessageChange={setNewMessage} onSendMessage={handleSendMessage} onRefreshMessages={handleRefreshMessages} sending={sending} isManualRefreshing={isManualRefreshing} tenantId={effectiveTenantId || ''} customerId={customerId} customerPhone={customerPhone} aiMode={aiMode} isWindowClosed={!isWindowOpen && !!customerPhone} onOpenTemplateSelector={() => setShowTemplateSelector(true)} />
        )}
      </div>

    </div>;
}